import { isString } from 'lodash';
import find from 'lodash/find';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import sortBy from 'lodash/sortBy';
import { getFramework, IFramework, trackConfig } from '@/framework';
import { getRotatePosition } from '@/utils/collision-kit';
import { pointsDistance } from '@/utils/math-kit';
import { ILayerProps, IPoint, LayerType } from '@/version-types';

interface IPointVariant extends IPoint {
    id?: string;
}

function getTrackFunc(type: string) {
    const groupAlign = trackConfig[LayerType.GROUP_LAYER]?.groupAlign;
    return isString(groupAlign) ? groupAlign : groupAlign?.[type];
}

/**
 * 多图层对齐
 * @returns
 */
export class AlignGroupLayers {
    private framework: IFramework;
    constructor(private id: string) {
        this.framework = getFramework(id);
    }

    /** 获取需要对齐的图层信息 */
    getAlignLayer(id: string) {
        const layer = this.framework.getLayer(id);
        if (layer) {
            return {
                layer,
                oldInfo: this.framework.getLayer(layer.id, true),
            };
        }
        return {
            layer,
        };
    }

    /** 检测图层是否在主舞台内 */
    // const checkInStage = (points: IPoint[]) => {
    //     const minX = minBy(points, (pos) => pos.x) as IPoint;
    //     const maxX = maxBy(points, (pos) => pos.x) as IPoint;
    //     const minY = minBy(points, (pos) => pos.y) as IPoint;
    //     const maxY = maxBy(points, (pos) => pos.y) as IPoint;
    //     const curLeft = minX.x;
    //     const curTop = minY.y;
    //     const curWidth = maxX.x - minX.x;
    //     const curHeight = maxY.y - minY.y;
    //     const { left, top, width, height } = mainLayer;
    //     return (
    //         inRange(curLeft, left - curWidth, left + width) &&
    //         inRange(curTop, top - curHeight, top + height)
    //     );
    // };

    /** 更新图层组信息 */
    updateGroupLayer(layer: ILayerProps, positions: IPoint[]) {
        if (!positions.length) return;
        const minX = minBy(positions, (pos) => pos.x) as IPoint;
        const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
        const minY = minBy(positions, (pos) => pos.y) as IPoint;
        const maxY = maxBy(positions, (pos) => pos.y) as IPoint;
        layer.left = minX.x;
        layer.top = minY.y;
        layer.width = maxX.x - minX.x;
        layer.height = maxY.y - minY.y;
        layer.rotate = 0;
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        return layer;
    }

    updateChildrenLayer(layer: ILayerProps, offsetLeft = 0, offsetTop = 0) {
        (layer.children || []).forEach((item) => {
            item.left = item.left + offsetLeft;
            item.top = item.top + offsetTop;
            this.updateChildrenLayer(item, offsetLeft, offsetTop);
        });
    }

    /**
     * 左对齐
     */
    left(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            // if (checkInStage(pos)) {
            positions.push(...pos);
            // }
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const minPoint = minBy(positions, (pos) => pos.x) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === minPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const min = minBy(point, (pos) => pos.x) as IPoint;
            const offsetX = min.x - minPoint.x;
            const offsetPoints = point.map((i) => {
                return { x: i.x - offsetX, y: i.y };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldLeft = item.left;
            const newLeft = item.left - offsetX;
            const offX = newLeft - oldLeft;
            this.updateChildrenLayer(item, offX);
            item.left = newLeft;
        });
        const newLayer = this.updateGroupLayer(layer, newLayerPositions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('left'));
        }
    }

    /**
     * 右对齐
     */
    right(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            // if (checkInStage(pos)) {
            positions.push(...pos);
            // }
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const maxPoint = maxBy(positions, (pos) => pos.x) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === maxPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const max = maxBy(point, (pos) => pos.x) as IPoint;
            const offsetX = max.x - maxPoint.x;
            const offsetPoints = point.map((i) => {
                return { x: i.x - offsetX, y: i.y };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldLeft = item.left;
            const newLeft = item.left - offsetX;
            const offX = newLeft - oldLeft;
            this.updateChildrenLayer(item, offX);
            item.left = newLeft;
        });
        const newLayer = this.updateGroupLayer(layer, newLayerPositions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('right'));
        }
    }

    /**
     * 顶部对齐
     */
    top(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            // if (checkInStage(pos)) {
            positions.push(...pos);
            // }
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const minPoint = minBy(positions, (pos) => pos.y) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === minPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const min = minBy(point, (pos) => pos.y) as IPoint;
            const offsetY = min.y - minPoint.y;
            const offsetPoints = point.map((i) => {
                return { x: i.x, y: i.y - offsetY };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldTop = item.top;
            const newTop = item.top - offsetY;
            const offY = newTop - oldTop;
            this.updateChildrenLayer(item, 0, offY);
            item.top = newTop;
        });
        const newLayer = this.updateGroupLayer(layer, newLayerPositions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('top'));
        }
    }

    /**
     * 底部对齐
     */
    bottom(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        console.log(layer);

        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            // if (checkInStage(pos)) {
            positions.push(...pos);
            // }
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const maxPoint = maxBy(positions, (pos) => pos.y) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === maxPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const max = maxBy(point, (pos) => pos.y) as IPoint;
            const offsetY = max.y - maxPoint.y;
            const offsetPoints = point.map((i) => {
                return { x: i.x, y: i.y - offsetY };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldTop = item.top;
            const newTop = item.top - offsetY;
            const offY = newTop - oldTop;
            this.updateChildrenLayer(item, 0, offY);
            item.top = newTop;
        });
        const newLayer = this.updateGroupLayer(layer, newLayerPositions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('bottom'));
        }
    }

    /**
     * 水平居中
     */
    verticalCenter(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const resList: IPoint[] = [];
        const { left, top, width, height } = layer;
        const groupLayerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
        (layer.children || []).forEach((item, index) => {
            // const pos = getRotatePosition(item);
            // if (checkInStage(pos)) {
            const { left, top, width, height } = item;
            const layerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
            resList.push({
                x: pointsDistance(layerCenter, groupLayerCenter),
                y: index,
            });
            // }
        });
        if (!resList.length) return;
        const minRes = minBy(resList, (item) => item.x);
        if (!minRes) return;
        const baseLayer = layer.children[minRes.y] as ILayerProps;
        const { left: l, top: t, width: w, height: h } = baseLayer;
        const baseCenter: IPoint = { x: w / 2 + l, y: h / 2 + t };
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item) => {
            // const newLeft = baseCenter.x - item.width / 2;
            const newTop = baseCenter.y - item.height / 2;
            // updateChildrenLayer(item, newLeft - item.left, newTop - item.top);
            this.updateChildrenLayer(item, 0, newTop - item.top);
            // item.left = newLeft;
            item.top = newTop;
            positions.push(...getRotatePosition(item));
        });
        const newLayer = this.updateGroupLayer(layer, positions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('verticalCenter'));
        }
    }

    /**
     * 垂直居中
     */
    horizontalCenter(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const resList: IPoint[] = [];
        const { left, top, width, height } = layer;
        const groupLayerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
        (layer.children || []).forEach((item, index) => {
            // const pos = getRotatePosition(item);
            // if (checkInStage(pos)) {
            const { left, top, width, height } = item;
            const layerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
            resList.push({
                x: pointsDistance(layerCenter, groupLayerCenter),
                y: index,
            });
            // }
        });
        if (!resList.length) return;
        const minRes = minBy(resList, (item) => item.x);
        if (!minRes) return;
        const baseLayer = layer.children[minRes.y] as ILayerProps;
        const { left: l, top: t, width: w, height: h } = baseLayer;
        const baseCenter: IPoint = { x: w / 2 + l, y: h / 2 + t };
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item) => {
            const newLeft = baseCenter.x - item.width / 2;
            this.updateChildrenLayer(item, newLeft - item.left);
            item.left = newLeft;
            positions.push(...getRotatePosition(item));
        });
        const newLayer = this.updateGroupLayer(layer, positions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('horizontalCenter'));
        }
    }

    /**
     * 水平分布
     */
    horizontalDistribution(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const children = layer.children || [];
        if (children.length <= 2) return;
        const resList: IPointVariant[] = [];
        // 过滤组套组不能使用分布
        // let disabled = false;
        (layer.children || []).forEach((item, index) => {
            const pos = getRotatePosition(item);
            const point = minBy(pos, (p) => p.x) as IPoint;
            resList.push({
                x: point.x,
                y: index,
                id: item.id,
            });
            // if (!disabled && item.layerType === LayerType.GROUP_LAYER) {
            //     disabled = true;
            // }
        });
        // if (disabled) return;
        const sortRes = sortBy(resList, (item) => item.x);
        const step = (sortRes[sortRes.length - 1].x - sortRes[0].x) / (sortRes.length - 1);
        const min = sortRes[0].x;
        sortRes.forEach((point, index) => {
            const item = children[point.y];
            const newX = min + index * step;
            const offX = newX - point.x;
            this.updateChildrenLayer(item, offX);
            item.left = item.left + offX;
        });
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item, index) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
        });
        const newLayer = this.updateGroupLayer(layer, positions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('horizontalDistribution'));
        }
    }

    /**
     * 垂直分布
     */
    verticalDistribution(id: string) {
        const { layer, oldInfo } = this.getAlignLayer(id);
        if (!layer || !oldInfo) return;
        const children = layer.children || [];
        if (children.length <= 2) return;
        const resList: IPointVariant[] = [];
        // 过滤组套组不能使用分布
        // let disabled = false;
        (layer.children || []).forEach((item, index) => {
            const pos = getRotatePosition(item);
            const point = minBy(pos, (p) => p.y) as IPoint;
            resList.push({
                x: point.y,
                y: index,
                id: item.id,
            });
            // if (!disabled && item.layerType === LayerType.GROUP_LAYER) {
            //     disabled = true;
            // }
        });
        // if (disabled) return;
        const sortRes = sortBy(resList, (item) => item.x);
        const step = (sortRes[sortRes.length - 1].x - sortRes[0].x) / (sortRes.length - 1);
        const min = sortRes[0].x;
        sortRes.forEach((point, index) => {
            const item = children[point.y];
            const newY = min + index * step;
            const offY = newY - point.x;
            this.updateChildrenLayer(item, 0, offY);
            item.top = item.top + offY;
        });
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item, index) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
        });
        const newLayer = this.updateGroupLayer(layer, positions);
        if (newLayer) {
            this.framework.history.write(oldInfo, layer, getTrackFunc('verticalDistribution'));
        }
    }
}
